import {ChangeDetectionStrategy, Component, computed, DestroyRef, effect, Injectable, Injector, Input, runInInjectionContext, Signal, signal} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";

import {AksjeeierListeKomponent} from "../aksjeeier-liste/aksjeeier-liste";
import {Observable, tap} from "rxjs";
import {AksjeeierbokId, AksjeeierEnkelModell, AksjeeierId, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {AksjeeierDatatjeneste} from "felles-ng2/tjenester/datalager/AksjeeierDatatjeneste";
import {MatButtonModule} from "@angular/material/button";
import {MatDialog} from "@angular/material/dialog";
import {AksjeeiereRedigerBeholdningDialogKomponent} from "web-ng2/moduler/aksjeeierbok/modale/aksjeeiere-rediger-beholdning-dialog/aksjeeiere-rediger-beholdning-dialog";
import {MatIconModule} from "@angular/material/icon";
import {OpprettAksjeeierDialogKomponent} from "web-ng2/moduler/aksjeeierbok/modale/aksjeeier-opprett-dialog/opprett-aksjeeier-dialog";
import {C3GrafKomponent} from "web-ng2/delt/komponenter/c3-graf/c3-graf";
import {antallAksjerPerAksjeklasse} from "web-ng2/moduler/aksjeeierbok/komponenter/aksjeeier-liste/BeholdningModellBase";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import type {ChartConfiguration, PrimitiveArray} from "c3";
import {toSignal} from "@angular/core/rxjs-interop";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {DataOgMeta, mapTilData} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {MatMenuModule} from "@angular/material/menu";
import {AksjeeiereSlettSisteBeholdningsendringDialogKomponent} from "web-ng2/moduler/aksjeeierbok/modale/aksjeeiere-slett-siste-beholdningsendring-dialog/aksjeeiere-slett-siste-beholdningsendring-dialog";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {LasterDataKomponent} from "web-ng2/delt/komponenter/laster-data/laster-data";
import {TranslateModule} from "@ngx-translate/core";
import {Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {AksjeeierbokEksportDialogKomponent} from "web-ng2/moduler/aksjeeierbok/modale/aksjeeierbok-eksport-dialog/aksjeeierbok-eksport-dialog";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {FormsModule} from "@angular/forms";
import {aksjeeierHarBeholdning} from "felles-ng2/modell/aksjeeierModellHjelpere";
import {GruppeDatatjeneste} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {OpprettAksjeeiereFraSkatteetatenDialogKomponent} from "web-ng2/moduler/aksjeeierbok/modale/opprett-aksjeeiere-fra-skatteetaten-dialog/opprett-aksjeeiere-fra-skatteetaten-dialog";
import {OpprettAksjeeiereFraSkatteetatenOrgnummerDialogKomponent} from "web-ng2/moduler/aksjeeierbok/modale/opprett-aksjeeiere-fra-skatteetaten-orgnummer-dialog/opprett-aksjeeiere-fra-skatteetaten-orgnummer-dialog";


const debug = initDebugger('AksjeeierbokComponent');

@Component({
    selector: 'aksjeeierbok',
    templateUrl: './aksjeeierbok.html',
    styleUrls: ['./aksjeeierbok.scss'],
    imports: [
        AksjeeierListeKomponent,
        MatButtonModule,
        MatIconModule,
        C3GrafKomponent,
        TipsKomponent,
        MatMenuModule,
        MatProgressSpinnerModule,
        LasterDataKomponent,
        TranslateModule,
        MatSlideToggle,
        FormsModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AksjeeierbokKomponent {
    static ajsSelector = 'aksjeeierbok';

    private _id!:AksjeeierbokId;

    guiStatuser = GuiStatus;
    alleAksjeeiere!: Signal<AksjeeierEnkelModell[]>;
    aksjeeiere =  computed(() => {
        if (this.skjulUtenBeholdning()){
            return this.alleAksjeeiere().filter(eier => aksjeeierHarBeholdning(eier));
        } else {
            return this.alleAksjeeiere();
        }
    });
    guiStatus = signal(GuiStatus.laster);
    harBeholdninger = signal(false);
    aksjeeiereDonutKonf = signal<ChartConfiguration | null>(null);
    aksjeklasserDonutKonf = signal<ChartConfiguration | null>(null);
    skjulUtenBeholdning =  signal(false);
    tilgangTilRedigerBeholdning =  computed(() => this.brukerkontekstTilbyder.autentisertBruker().inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, this.gruppeDatatjeneste.aktivGruppeIkkeNull().id))
    tilgangTilNyAksjeeier =  computed(() => this.brukerkontekstTilbyder.autentisertBruker().inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, this.gruppeDatatjeneste.aktivGruppeIkkeNull().id))
    tilgangTilAngreSisteBeholdningsoppdatering =  computed(() => this.brukerkontekstTilbyder.autentisertBruker().inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, this.gruppeDatatjeneste.aktivGruppeIkkeNull().id))


    constructor(private avhengigheter: AksjeeierbokComponentAvhengigheter, private dialog: MatDialog, private injector:Injector, private oversettelser: Oversettelser, private gruppeDatatjeneste: GruppeDatatjeneste, private brukerkontekstTilbyder:BrukerkontekstTilbyder, private destroyRef:DestroyRef) {
        //effect nødvendig for å kompensere for tilsynelatende bug i chrome - der endring i guiStatus ikke trigger re-rendring
        //før annen event (feks musebevegelse) dersom man går til aksjeeierbok ved å klikke på dennes knapp i tab-listen.
        //Ved å kalle guiStatus inne i en effect forsvinner dette problemet.
        //Problemet oppstår ikke i safari/firefox.
        //Gjenskapes ved å (husk først å kommentere ut effecten):
        //- gå til medlemmer-arkfanen for en gruppe som har aksjeeierbok, re-last siden
        //- klikk på "aksjeeierbok"-knappen - pass på å ikke bevege musepekeren etter klikket
        //- nå vil spinner vises helt til du beveger musepeker ut av knappen
        effect(() => this.guiStatus());
    }

    /** id til forespurt aksjeeierbok, null dersom gruppen ikke har noen aksjeeierbok */
    @Input() set id(id: AksjeeierbokId) {
        if (erNil(id)) {
            this.guiStatus.set(GuiStatus.ingenAksjeeierbok);
            return;
        }
        this._id = id;
        //@Input metoder kjører ikke i noen injector kontekst - må sørge for det selv her, pga bruk av signal
        runInInjectionContext(this.injector, () => this.initAksjeeierbok(id));
    }

    get id() {
        return this._id;
    }

    visNyAksjeeierDialog(): void {
        const dialogRef = OpprettAksjeeierDialogKomponent.vis(this.dialog);
        dialogRef.afterClosed().subscribe(result => {
            // må vise alle, eller så ser de ikke den aksjeeieren de har opprettet.
            this.skjulUtenBeholdning.set(false);
            debug('visNyAksjeeierDialog, dialog lukket');
        });
    }

    redigerBeholdning() {
        AksjeeiereRedigerBeholdningDialogKomponent.vis(this.id, this.dialog);
    }

    lastNedAksjeeierbok(){
        AksjeeierbokEksportDialogKomponent.vis(this.id ,this.dialog)
    }

    visAngreSisteBeholdningsoppdateringDialog(){
        AksjeeiereSlettSisteBeholdningsendringDialogKomponent.vis(this.id, this.dialog);
    }

    importerFraSkatteetaten() {
        const orgnummerRef = OpprettAksjeeiereFraSkatteetatenOrgnummerDialogKomponent.vis(this.dialog, this.gruppeDatatjeneste.aktivGruppeIkkeNull().aksjeeierbokId!);
        orgnummerRef.afterClosed().subscribe(resultat => {
            if (erIkkeNil(resultat)) {
                const ref2 = OpprettAksjeeiereFraSkatteetatenDialogKomponent.vis(this.dialog, resultat, this.gruppeDatatjeneste.aktivGruppeIkkeNull().aksjeeierbokId!);
                ref2.afterClosed().subscribe(r => {
                    // resultat blir lagret i aksjeeierdatatjeneste, så skal ikke trenge å sjekke noe her.
                    console.log(r);
                })
            }
        })
    }

    //private hjelpemetoder

    private initAksjeeierbok(id: AksjeeierbokId) {
        debug('initAksjeeierbok', id);
        this.avhengigheter.setAktivAksjeeierbokId(id);

        this.alleAksjeeiere = toSignal(this.avhengigheter.finnAksjeeiereTilknyttet(id)
            .pipe(
              tap(eiere => this.aksjeeiereOppdatert(eiere)),
              mapTilData()),
          {initialValue: []});
    }

    private aksjeeiereOppdatert(aksjeeierer: DataOgMeta<AksjeeierEnkelModell[]>) {
        debug('aksjeeiereOppdatert', aksjeeierer)
        if (aksjeeierer.data.length == 0) {
            if (!aksjeeierer.meta.levertFraCache)
                this.guiStatus.set(GuiStatus.ingenAksjeeiere); //første kall skjer alltid med tom liste - mens data hentes fra server...
            return;
        }

        this.guiStatus.set(GuiStatus.harAksjeeiere);
        debug('aksjeeiereOppdatert guiStatus=harAksjeeiere')
        this.harBeholdninger.set(this.beregnHarBeholdninger(aksjeeierer.data));
        this.aksjeeiereDonutKonf.set(this.beregnAksjeeiereDonutKonf(aksjeeierer.data));
        this.aksjeklasserDonutKonf.set(this.beregnAksjeklasserDonutKonf(aksjeeierer.data));
    }

    private beregnAksjeklasserDonutKonf(aksjeeierer: AksjeeierEnkelModell[]) {
        const beholdninger = aksjeeierer
          .map(eier => ({
              aksjeklasse: eier.naavaerendeBeholdning?.aksjeklasse ?? null,
              antallAksjer: this.beholdning(eier)
          }));
        const klasser = antallAksjerPerAksjeklasse(beholdninger, this.oversettelser);

        const konf = this.donutKonf();
        konf.data.columns = [...klasser.entries()].map(innslag => [innslag[0].navn, innslag[1]]);
        return konf;
    }

    private beregnHarBeholdninger(aksjeeierer: AksjeeierEnkelModell[]){
        return aksjeeierer.some(eier => this.beholdning(eier) > 0);
    }

    private beregnAksjeeiereDonutKonf(aksjeeierer: AksjeeierEnkelModell[]) {
        const konf = this.donutKonf();
        // key er det unike navnet, value er aksjeeierId
        const unikeEierNavn = new Map<string, AksjeeierId>();
        aksjeeierer.forEach((eier) => {
            if (!unikeEierNavn.has(eier.navn)) {
                // navn er unikt - OK
                unikeEierNavn.set(eier.navn, eier.id);
                return;
            }
            let aksjeklasse = eier.naavaerendeBeholdning?.aksjeklasse?.navn
            if (!erNil(aksjeklasse)) {
                // legger til aksjeklasse og sjekker om det er unikt.
                if (!unikeEierNavn.has(eier.navn + ' ' + aksjeklasse)) {
                    // OK - Unikt med aksjeklasse
                    unikeEierNavn.set(eier.navn + ' ' + aksjeklasse, eier.id)
                    return
                }
                // kommer hit - legge til aksjeklasse var ikke nok.
            }

            // legg til tall for å gjøre unikt
            let i: number = 1;
            let navn = eier.navn + ' ' + i;
            while (unikeEierNavn.has(navn)) {
                i++;
                navn = eier.navn + ' ' + i;
            }
            // OK unikt med tall.
            unikeEierNavn.set(navn, eier.id)
        });

        let kolonner: [string, number][] = [];
        unikeEierNavn.forEach((aksjeeierId, navn) => kolonner.push([navn, this.beholdning(aksjeeierer.find((eier) => eier.id === aksjeeierId)!)]));
        konf.data.columns = kolonner;
        konf.donut!.label!.format = (value: number, ratio: number, id: string) => {
            return `${this.initialer(id)}`;
            //  return `${format('.1~%')(ratio)} (${this.initialer(id)})`;
        };
        return konf;
    }

    private donutKonf(): ChartConfiguration {
        return {
            legend: {
                position: 'right',
                hide: true
            },
            color: {
                pattern: [
                    //'#E3F2FD' /* 50 */,
                    //'#BBDEFB' /* '00 */,
                    //'#90CAF9' /* 200 */,
                    '#64B5F6' /* 300 */,
                    // '#42A5F5' /* 400 */,
                    '#2196F3' /* 500 */,
                    // '#1E88E5' /* 600 */,
                    '#1976D2' /* 700 */,
                    //'#1565C0' /* 800 */,
                    '#0D47A1' /* 900 */,
                    '#82B1FF' /* A100 */,
                    '#448AFF' /* A200 */,
                    // '#2979FF' /* A400 */,
                    '#2962FF' /* A700 */
                ]
            },
            donut: {
                label: {
                    format: (value: number, ratio: number, id: string) => id
                }
            },
            data: {
                columns: <Array<[string, ...PrimitiveArray]>>[], //må populeres av kaller
                type: 'donut',
            },
        };
    }

    private initialer(navn: string) {
        return navn
          .split(/\s+/)
          .map(segment => segment.charAt(0))
          .join('')
          .toUpperCase();
    }

    private beholdning(eier: AksjeeierEnkelModell){
        return eier.naavaerendeBeholdning?.antall ?? 0
    }
}

enum GuiStatus {
    laster, ingenAksjeeiere, harAksjeeiere, ingenAksjeeierbok
}

@Injectable()
export class AksjeeierbokComponentAvhengigheter {

    constructor(private aksjeeierDatatjeneste: AksjeeierDatatjeneste) {
    }

    finnAksjeeiereTilknyttet(aksjeeierbokId: AksjeeierbokId): Observable<DataOgMeta<AksjeeierEnkelModell[]>> {
        return this.aksjeeierDatatjeneste.finnAksjeeiereTilknyttetMedMeta(aksjeeierbokId);
    }

    setAktivAksjeeierbokId(id: AksjeeierbokId) {
        this.aksjeeierDatatjeneste.aktivAksjeeierbokId = id;
    }
}
